import * as ko from 'knockout';
import { API_CONFIG } from '../config';
import { createIncidentField } from '../interface/IncidentField';
import { getQueryParams, readAccessToken } from '../utils';
import { DeepLinkResolveError, readDeepLinkToken, buildDeepLinkResolveUrl, sanitizePrefill, deepLinkErrorMessage, deepLinkAppliedMessage, deepLinkLockHint, isDeepLinkTicketLinkable, } from '../deeplink/deepLinkContext';
class FormViewModel {
    // Constructor with dependency injection of MainViewModel
    constructor(mainViewModel) {
        this.mainViewModel = mainViewModel;
        // Observables for Step 2
        this.buildings = ko.observableArray([]);
        this.contracts = ko.observableArray([]);
        this.properties = ko.observableArray([]);
        this.allExternalProperties = ko.observableArray([]); // All properties from external system
        this.units = ko.observableArray([]); // New observable for units
        this.selectedResidents = ko.observable({});
        this.customResidents = ko.observable({});
        // Observables for service providers (Dienstleister)
        this.serviceProviders = ko.observableArray([]);
        this.selectedServiceProviders = ko.observable({});
        this.customServiceProviders = ko.observable({});
        this.isLoadingServiceProviders = ko.observable(false);
        // New observables for residents
        this.residents = ko.observableArray([]);
        this.selectedResident = ko.observable(null);
        this.selectedResidentLabel = ko.observable("");
        this.isLoadingResidents = ko.observable(false);
        this.isFetchingDetails = ko.observable(false); // New observable for contact details fetching status
        this.selectedBuilding = ko.observable(null);
        this.selectedContract = ko.observable(null);
        this.selectedProperty = ko.observable(null);
        this.selectedExternalProperty = ko.observable(null); // Selected property from the full list dropdown
        this.selectedUnit = ko.observable(null); // New observable for selected unit
        this.selectedBuildingLabel = ko.observable("");
        this.selectedContractLabel = ko.observable("");
        this.selectedPropertyLabel = ko.observable(""); // Updated for property label
        this.selectedExternalPropertyLabel = ko.observable(""); // Label for external property dropdown
        this.selectedUnitLabel = ko.observable(""); // New observable for selected unit label
        this.externalId = ko.observable("");
        this.selectedExternalTicketDescription = ko.observable("");
        this.tenantId = ko.observable("");
        this.availableTickets = ko.observableArray([]);
        this.showExternalIdSelect = ko.observable(false);
        // Deep-Link (Stufe 1): Kontext aus einem CRM-Vorgang, Formular geöffnet mit ?ctx=<token>
        this.deepLinkToken = null;
        this.deepLinkContext = ko.observable(null);
        this.deepLinkStatus = ko.observable('idle');
        this.deepLinkMessage = ko.observable('');
        /** Nutzer hat die Sperre der aus dem CRM übernommenen Felder aufgehoben („Vorbelegung aufheben"). */
        this.deepLinkUnlocked = ko.observable(false);
        /** Sperre aktiv, solange ein Kontext übernommen ist und der Nutzer sie nicht aufgehoben hat. */
        this.deepLinkLockActive = ko.pureComputed(() => {
            const ctx = this.deepLinkContext();
            const status = this.deepLinkStatus();
            return !!ctx && !this.deepLinkUnlocked() && status !== 'idle' && status !== 'error';
        });
        /** Ein Feld ist gesperrt, solange sein Wert dem Wert aus dem CRM-Vorgang entspricht. */
        this.deepLinkBuildingLocked = ko.pureComputed(() => {
            var _a;
            const ctx = this.deepLinkContext();
            return this.deepLinkLockActive() && !!ctx && !!ctx.buildingId && String((_a = this.selectedBuilding()) !== null && _a !== void 0 ? _a : '') === String(ctx.buildingId);
        });
        this.deepLinkPropertyLocked = ko.pureComputed(() => {
            var _a;
            const ctx = this.deepLinkContext();
            return this.deepLinkLockActive() && !!ctx && !!ctx.propertyId && String((_a = this.selectedExternalProperty()) !== null && _a !== void 0 ? _a : '') === String(ctx.propertyId);
        });
        this.deepLinkUnitLocked = ko.pureComputed(() => {
            var _a;
            const ctx = this.deepLinkContext();
            return this.deepLinkLockActive() && !!ctx && !!ctx.unitId && String((_a = this.selectedUnit()) !== null && _a !== void 0 ? _a : '') === String(ctx.unitId);
        });
        this.deepLinkTicketLocked = ko.pureComputed(() => {
            var _a;
            const ctx = this.deepLinkContext();
            return this.deepLinkLockActive() && !!ctx && isDeepLinkTicketLinkable(ctx) && String((_a = this.externalId()) !== null && _a !== void 0 ? _a : '') === String(ctx.externalTicketId);
        });
        /** Ist überhaupt ein Feld gesperrt? Nur dann zeigt das Banner den Sperr-Hinweis mit „Vorbelegung aufheben". */
        this.deepLinkAnyFieldLocked = ko.pureComputed(() => this.deepLinkBuildingLocked() || this.deepLinkPropertyLocked() || this.deepLinkUnitLocked() || this.deepLinkTicketLocked());
        /** Der Zuordnungs-Satz stimmt nur, wenn auch der Vorgang übernommen (und damit verknüpft) wird. */
        this.deepLinkLockText = ko.pureComputed(() => (this.deepLinkTicketLocked()
            ? 'Die übernommenen Felder sind gesperrt, damit der Schaden dem richtigen Vorgang zugeordnet bleibt.'
            : 'Die aus dem CRM übernommenen Felder sind gesperrt.'));
        /** Tooltip der gesperrten Felder (Herkunft + Hinweis auf „Vorbelegung aufheben"). */
        this.deepLinkLockHint = ko.pureComputed(() => {
            const ctx = this.deepLinkContext();
            return ctx ? deepLinkLockHint(ctx) : '';
        });
        this.unlockDeepLink = () => {
            this.deepLinkUnlocked(true);
        };
        this.deepLinkUnitSubscriptions = [];
        this.deepLinkUnitTimer = null;
        /** Kontext wird höchstens zweimal angewandt: initial + einmal nach einer späteren Gebäudewahl (Wiederherstellung). */
        this.deepLinkApplications = 0;
        /** Melder („Gemeldet von") wird je Kontext höchstens einmal als betroffene(r) Mieter/Eigentümer vorbelegt. */
        this.deepLinkResidentApplied = false;
        this.deepLinkResidentSubscription = null;
        this.deepLinkBuildingPreselected = false;
        this.incidentCategories = ko.observableArray([]);
        this.selectedLevel1Category = ko.observable(null);
        this.selectedLevel2Category = ko.observable(null);
        this.selectedLevel3Category = ko.observable(null);
        this.level2Categories = ko.observableArray([]);
        this.level3Categories = ko.observableArray([]);
        this.incidentFields = ko.observableArray([]);
        this.requestMaker = ko.observable(null); // Storing the requestMaker object
        this.isSubmitting = ko.observable(false); // Flag to track submission state
        this.isSubmittedSuccessfully = ko.observable(false); // Flag to track successful submission
        this.isLoadingBuildings = ko.observable(false); // Flag to track loading state of buildings
        this.isLoadingUnits = ko.observable(false); // Einheitenliste des gewählten Objekts wird geladen
        /** Abschluss-Signal von fetchUnits: für welches Objekt die Einheitenliste zuletzt fertig geladen wurde (auch bei Fehler). */
        this.unitsLoadedFor = ko.observable(null);
        this.unitsRequestSeq = 0;
        this.mappingRequestSeq = 0;
        this.isLoadingAllProperties = ko.observable(false); // Flag to track loading state of all external properties
        this.dropdownVisible = ko.observable(false); // Control dropdown visibility
        this.externalPropertyDropdownVisible = ko.observable(false); // Control external property dropdown visibility
        this.buildingSearchQuery = ko.observable(""); // New observable for search input
        this.externalPropertySearchQuery = ko.observable(""); // Search query for external properties
        // Debounce mechanism for autocomplete to prevent too many API calls
        this.buildingSearchQueryDelayed = ko.pureComputed(this.buildingSearchQuery)
            .extend({ rateLimit: { method: "notifyWhenChangesStop", timeout: 300 } });
        // Array of key-value pairs for URL parameters - updated to include resident
        this.urlParams = ko.observableArray([
            { urlkey: 'Versicherungsnummer', observableName: 'selectedContractLabel' },
            { urlkey: 'Vorgangsnummer', observableName: 'externalId' },
            { urlkey: 'Vorgang', observableName: 'selectedExternalTicketDescription' },
            { urlkey: 'Bewohner', observableName: 'selectedResidentLabel' } // Add resident to URL params
        ]);
        this.fetchAvailableTickets = () => {
            const buildingId = this.selectedBuilding();
            const unitId = this.selectedUnit();
            const profileId = localStorage.getItem('profileId');
            const externalSystem = localStorage.getItem('externalSystem') || '';
            if (!buildingId || !unitId || !profileId) {
                return;
            }
            if (!externalSystem) {
                this.mainViewModel.notification('Fehler: Externes System ist nicht unterstützt.');
                return;
            }
            const url = `${API_CONFIG.baseUrl}/api/${externalSystem}/buildings/${buildingId}/units/${unitId}/available-tickets?profileId=${profileId}`;
            const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
            const accessToken = readAccessToken();
            this.fetchWithAuthorization(url, {
                headers: {
                    'X-API-Key': licenseKey,
                    'Authorization': `Bearer ${accessToken}`
                }
            })
                .then(response => response.json())
                .then(data => {
                this.availableTickets(data);
                this.showExternalIdSelect(true);
            })
                .catch(error => {
                console.error('Error fetching available tickets:', error);
                this.mainViewModel.notification('Fehler beim Laden der verfügbaren Tickets.');
            });
        };
        // Computed observable to filter and format buildings with bold matching letters
        this.filteredBuildings = ko.computed(() => {
            const query = this.buildingSearchQuery().toLowerCase();
            return this.buildings().map(building => {
                const address = building.address || ""; // Ensure address is a string
                let formattedAddress = address;
                if (query && address.toLowerCase().includes(query)) {
                    const startIndex = address.toLowerCase().indexOf(query);
                    const endIndex = startIndex + query.length;
                    formattedAddress = `${address.slice(0, startIndex)}<strong>${address.slice(startIndex, endIndex)}</strong>${address.slice(endIndex)}`;
                }
                return Object.assign(Object.assign({}, building), { formattedAddress: formattedAddress });
            });
        });
        this.selectBuilding = (building) => {
            console.log('Selected building:', building);
            this.selectedBuilding(building.id);
            this.buildingSearchQuery(building.address); // Set the search query to the selected address
            this.dropdownVisible(false); // Hide the dropdown after selection
            // Reset resident selection when building changes
            this.selectedResident(null);
            this.selectedResidentLabel('');
        };
        // New handler for resident selection
        this.onResidentSelected = (selectedResidentId) => {
            if (selectedResidentId === null) {
                this.selectedResidentLabel('');
                return;
            }
            const resident = this.residents().find(r => Number(r.id) === selectedResidentId);
            if (resident) {
                this.selectedResidentLabel(resident.name || '');
                // If tenant ID should be set based on resident selection, uncomment this:
                // this.tenantId(resident.id.toString());
            }
        };
        this.onUnitSelected = (selectedUnitId) => {
            if (selectedUnitId === null) {
                this.selectedUnitLabel('');
                return;
            }
            const unit = this.units().find(u => u.id === selectedUnitId);
            if (unit) {
                this.selectedUnitLabel(unit.name);
            }
        };
        this.showDropdown = () => {
            this.dropdownVisible(true);
        };
        this.hideDropdown = () => {
            this.dropdownVisible(false);
        };
        this.hideDropdownDelayed = () => {
            setTimeout(() => {
                this.dropdownVisible(false);
            }, 200); // Delay to allow for click event to register
        };
        // Computed observable to filter external properties based on search query
        this.filteredExternalProperties = ko.computed(() => {
            const query = this.externalPropertySearchQuery().toLowerCase();
            return this.allExternalProperties()
                .filter((property) => {
                if (!query)
                    return true;
                const name = property.name || property.displayName || `Property ${property.id}`;
                return name.toLowerCase().includes(query);
            })
                .map((property) => {
                const name = property.name || property.displayName || `Property ${property.id}`;
                let formattedName = name;
                if (query && name.toLowerCase().includes(query)) {
                    const startIndex = name.toLowerCase().indexOf(query);
                    const endIndex = startIndex + query.length;
                    formattedName = `${name.slice(0, startIndex)}<strong>${name.slice(startIndex, endIndex)}</strong>${name.slice(endIndex)}`;
                }
                return Object.assign(Object.assign({}, property), { formattedName: formattedName });
            });
        });
        this.showExternalPropertyDropdown = () => {
            this.externalPropertyDropdownVisible(true);
        };
        this.hideExternalPropertyDropdownDelayed = () => {
            setTimeout(() => {
                this.externalPropertyDropdownVisible(false);
            }, 200);
        };
        this.selectExternalProperty = (property) => {
            var _a;
            console.log('Selected external property:', property);
            const unchanged = String((_a = this.selectedExternalProperty()) !== null && _a !== void 0 ? _a : '') === String(property.id);
            this.selectedExternalProperty(property.id);
            // Dasselbe Objekt erneut gewählt, nachdem seine Einheiten nicht geladen werden konnten → neu laden (die unveränderte
            // Auswahl löst sonst nichts aus, und die leere Einheitenliste ließe keine Wahl)
            const loaded = this.unitsLoadedFor();
            if (unchanged && loaded && !loaded.ok && loaded.propertyId === String(property.id) && !this.isLoadingUnits()) {
                this.fetchUnits(property.id);
            }
            const name = property.name || property.displayName || `Property ${property.id}`;
            this.externalPropertySearchQuery(name); // Set the search query to the selected name
            this.externalPropertyDropdownVisible(false); // Hide the dropdown after selection
        };
        this.updateLevel2Categories = (incidentCategoryId) => {
            // Find the category object that matches the selected ID
            const selectedCategory = this.incidentCategories().find(cat => cat.id === incidentCategoryId);
            if (selectedCategory && selectedCategory.children) {
                this.level2Categories(selectedCategory.children);
                this.level3Categories([]); // Reset level 3 categories
                this.selectedLevel2Category(null); // Reset level 2 selected category
            }
            else {
                this.level2Categories([]);
                this.level3Categories([]);
            }
        };
        this.updateLevel3Categories = () => {
            var _a, _b;
            // Find the category object that matches the selected ID
            const selectedCategory = (_b = (_a = this.incidentCategories().find(x => x.id === this.selectedLevel1Category())) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.find(x => x.id === this.selectedLevel2Category());
            if (selectedCategory && selectedCategory.children) {
                this.level3Categories(selectedCategory.children);
            }
            else {
                this.level3Categories([]);
            }
        };
        this.onBuildingSelected = (selectedBuildingId) => {
            if (selectedBuildingId === null) {
                this.selectedBuildingLabel('');
                // Properties, units, etc., are cleared by fetchExternalProperties
                // and contracts by fetchContracts when selectedBuildingId becomes null.
                return;
            }
            // Find the selected building and update its label
            const building = this.buildings().find(b => b.id === selectedBuildingId);
            if (building) {
                this.selectedBuildingLabel(building.address);
            }
        };
        this.onContractSelected = (selectedContractId) => {
            if (selectedContractId === null) {
                this.selectedContractLabel('');
                return;
            }
            const contract = this.contracts().find(c => c.id === selectedContractId);
            if (contract) {
                this.selectedContractLabel(contract.name);
            }
        };
        this.onPropertySelected = (selectedPropertyId) => {
            if (selectedPropertyId === null) {
                this.selectedPropertyLabel('');
                return;
            }
            const property = this.properties().find(p => p.id === selectedPropertyId);
            if (property) {
                // When a property is selected, we might want to store its displayName or original name.
                // For now, let's assume the label should reflect the displayName.
                this.selectedPropertyLabel(property.displayName);
            }
        };
        this.conditionallySendForm = () => {
            const categoryPath = this.constructCategoryPath();
            const externalSystem = localStorage.getItem('externalSystem') || '';
            const formData = {
                buildingId: this.selectedBuilding(),
                contractId: this.selectedContract(),
                categoryPath: categoryPath,
                externalId: this.externalId(),
                tenantId: this.tenantId(),
                residentId: this.selectedResident(),
                propertyId: this.selectedProperty(),
                unitId: this.selectedUnit(),
                sourceSystem: externalSystem
            };
            // Process requestMaker data
            const processedRequestMaker = this.processRequestMakerData(this.requestMaker());
            // Combine formData and processedRequestMaker
            const submissionData = Object.assign(Object.assign({}, formData), processedRequestMaker);
            console.log(submissionData);
            // Get the license key from loginFormViewModel
            const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
            if (API_CONFIG.debugMode) {
                // Mock the API response when in debug mode
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve({
                            json: () => Promise.resolve({
                                message: 'Mocked submission successful',
                                mockData: submissionData // Optionally include the mocked data for debugging purposes
                            })
                        });
                    }, 3000);
                });
            }
            // Retrieve the accessToken from local storage
            const accessToken = readAccessToken();
            // Actual API call when not in debug mode
            return this.fetchWithAuthorization(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.submitForm}?category=${categoryPath}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-provider': 'idwell',
                    'x-tenant-id': 'incon',
                    'Authorization': `Bearer ${accessToken}`,
                    'X-API-Key': licenseKey // Include the license key in the header
                },
                body: JSON.stringify(submissionData)
            });
        };
        this.submitForm = () => {
            if (this.isSubmitting()) {
                return; // Prevent multiple submissions
            }
            const firstUnfilledField = this.incidentFields().find(field => {
                if (!field.required) {
                    return false;
                }
                const value = field.value();
                return value === null || value === undefined || value === '';
            });
            if (firstUnfilledField) {
                this.mainViewModel.notification('Bitte füllen Sie alle Pflichtfelder aus.');
                let elementToFocus = null;
                // Find the element associated with the field
                const fieldName = firstUnfilledField.name;
                // For resident / service-provider fields, the element is the multi-select component
                if (this.isResidentField(fieldName) || this.isServiceProviderField(fieldName)) {
                    // The component's root is a details tag with a specific id
                    elementToFocus = document.getElementById('details-' + fieldName);
                }
                else {
                    // For standard inputs, we can select by name
                    elementToFocus = document.querySelector(`[name="${fieldName}"]`);
                }
                if (elementToFocus) {
                    // Scroll to the element and focus it
                    elementToFocus.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    elementToFocus.focus();
                }
                return;
            }
            this.isSubmitting(true); // Set submitting flag to true
            // Save special field values to localStorage before submitting
            this.incidentFields().forEach(field => {
                const fieldNameLower = field.name.toLowerCase();
                const fieldValue = field.value();
                if (fieldNameLower.endsWith('_nameap')) {
                    this.saveToLocalStorage('user_name', fieldValue);
                }
                else if (fieldNameLower.endsWith('_emailap')) {
                    this.saveToLocalStorage('user_email', fieldValue);
                }
                else if (fieldNameLower.endsWith('_nummer') || fieldNameLower.endsWith('_nummerap')) {
                    this.saveToLocalStorage('user_phone', fieldValue);
                }
            });
            this.conditionallySendForm()
                .then(response => {
                // Check if the response is ok (status in the range 200-299)
                if (response instanceof Response && !response.ok) {
                    // Extract error message from the response
                    return response.text().then(text => {
                        throw new Error(text || `Error: ${response.status} ${response.statusText}`);
                    });
                }
                return response.json();
            })
                .then(data => {
                console.log('Submission successful', data);
                // Handle successful submission (e.g., navigate to a confirmation page or show a success message)
                // Set successful submission flag to true only on success
                this.isSubmittedSuccessfully(true);
            })
                .catch(error => {
                console.error('Submission failed', error);
                // Show error message to the user
                this.mainViewModel.notification(error.message || "Fehler beim Anlegen des Schadens");
                // Set successful submission flag to false on error
                this.isSubmittedSuccessfully(false);
            })
                .finally(() => {
                this.isSubmitting(false); // Reset submitting flag
            });
        };
        this.mainViewModel.isUserAuthenticated.subscribe((authenticated) => {
            if (!authenticated) {
                // Abmeldung (z. B. 401): eine noch wartende Deep-Link-Einheitenwahl darf nach der Neuanmeldung nicht mit dem
                // alten Kontext weiterlaufen
                this.disposeDeepLinkUnitSubscription();
            }
            if (authenticated) {
                // Initialize data for buildings  
                const buildingsLoaded = this.fetchBuildings();
                // Fetch all external properties at login
                const externalPropertiesLoaded = this.fetchAllExternalProperties();
                // Deep-Link: Kontext erst auflösen, wenn Gebäude + externe Objekte geladen sind (Vorauswahl braucht beide Listen)
                if (this.deepLinkToken) {
                    Promise.all([buildingsLoaded, externalPropertiesLoaded])
                        .catch(() => undefined)
                        .then(() => this.resolveDeepLinkContext());
                }
                // Fetch the mandant-wide service provider (Dienstleister) list at login
                this.fetchServiceProviders();
                // Fetch incident categories
                this.fetchIncidentCategories();
                // Subscription to handle the autocomplete search behavior
                this.buildingSearchQueryDelayed.subscribe(query => {
                    if (query.length >= 3) {
                        this.fetchBuildings(query); // Fetch buildings with address filter when query is 3 characters or more
                    }
                    else if (query.length === 0) {
                        this.fetchBuildings(); // Fetch all buildings if the input is cleared
                    }
                    else {
                        this.buildings([]); // Clear the listbox if input is less than 3 characters (but not empty)
                    }
                });
            }
        });
        // Subscribe to changes in selectedBuilding to update contracts and residents
        this.selectedBuilding.subscribe(this.onBuildingSelected);
        this.selectedBuilding.subscribe(this.fetchContracts.bind(this));
        this.selectedBuilding.subscribe(this.fetchExternalProperties.bind(this)); // Call when selectedBuilding changes
        this.selectedContract.subscribe(this.onContractSelected);
        // When the user selects from the "Gebäude (externes System)" dropdown, drive selectedProperty
        this.selectedExternalProperty.subscribe((propertyId) => {
            if (propertyId !== null && propertyId !== undefined) {
                // Sync selectedProperty from the external property dropdown
                this.selectedProperty(propertyId);
                // Update label
                const prop = this.allExternalProperties().find((p) => String(p.id) === String(propertyId));
                if (prop) {
                    const name = prop.name || prop.displayName || `Property ${prop.id}`;
                    this.selectedExternalPropertyLabel(name);
                    if (this.externalPropertySearchQuery() !== name) {
                        this.externalPropertySearchQuery(name);
                    }
                }
            }
            else {
                this.selectedProperty(null);
                this.selectedExternalPropertyLabel('');
                this.externalPropertySearchQuery('');
            }
        });
        this.selectedProperty.subscribe(this.onPropertySelected);
        this.selectedProperty.subscribe((propertyId) => {
            if (propertyId) {
                this.fetchUnits(propertyId);
                this.fetchResidents(); // Fetch residents when property changes
            }
            else {
                this.unitsRequestSeq++; // eine noch laufende Einheiten-Anfrage darf die geleerte Liste nicht wieder füllen
                this.isLoadingUnits(false);
                this.units([]);
                this.selectedUnit(null);
            }
        });
        this.selectedUnit.subscribe(this.onUnitSelected); // New subscription for unit selection
        this.selectedResident.subscribe(this.onResidentSelected); // New subscription for resident selection
        this.selectedBuilding.subscribe(() => {
            this.availableTickets([]);
            this.showExternalIdSelect(false);
        });
        this.selectedUnit.subscribe(() => {
            this.availableTickets([]);
            this.showExternalIdSelect(false);
        });
        this.selectedLevel1Category.subscribe(this.updateLevel2Categories);
        this.selectedLevel2Category.subscribe(this.updateLevel3Categories);
        // Fetch external ID from query parameters and populate the observable
        const params = getQueryParams();
        if (params['externalid']) {
            this.externalId(params['externalid']);
        }
        // Deep-Link-Token (?ctx=…) merken — aufgelöst wird er nach der Anmeldung (siehe isUserAuthenticated)
        this.deepLinkToken = readDeepLinkToken(params);
        if (this.deepLinkToken) {
            this.deepLinkStatus('loading');
            this.deepLinkMessage('Vorgang wird nach der Anmeldung aus dem CRM geladen …');
        }
        this.externalId.subscribe((selectedId) => {
            const selectedTicket = this.availableTickets().find(ticket => ticket.id === selectedId);
            if (selectedTicket) {
                this.selectedExternalTicketDescription(selectedTicket.name);
            }
            else {
                this.selectedExternalTicketDescription("");
            }
        });
        // Fetch tenant ID from query parameters and populate the observable
        if (params['tenantid']) {
            this.tenantId(params['tenantid']);
        }
        // Add computed observable to check if the form for Step 2 is valid
        this.isStep2FormValid = ko.computed(() => {
            const level2Visible = this.level2Categories().length > 0;
            const level3Visible = this.level3Categories().length > 0;
            const level2Valid = !level2Visible || (level2Visible && !!this.selectedLevel2Category());
            const level3Valid = !level3Visible || (level3Visible && !!this.selectedLevel3Category());
            return !!(this.selectedBuilding() &&
                this.selectedContract() &&
                this.selectedProperty() &&
                this.selectedUnit() &&
                this.selectedLevel1Category() &&
                level2Valid &&
                level3Valid);
        });
        this.showExternalIdLink = ko.computed(() => {
            return !!(this.selectedBuilding() && this.selectedUnit());
        });
        this.areRequiredFieldsFilled = ko.computed(() => {
            return this.incidentFields().every(field => {
                if (!field.required) {
                    return true; // Not required, so it's valid
                }
                const value = field.value();
                return value !== null && value !== undefined && value !== '';
            });
        });
        // Subscribe to changes in observables to update the URL
        this.urlParams().forEach(param => {
            const observable = this[param.observableName];
            if (ko.isObservable(observable)) {
                observable.subscribe((newValue) => {
                    this.updateUrlParam(param.urlkey, newValue);
                });
            }
        });
    }
    // Function to fetch buildings, optionally with a search query for the address
    fetchBuildings(searchQuery) {
        this.isLoadingBuildings(true); // Set loading flag to true
        let url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.buildings}`;
        const params = new URLSearchParams();
        if (searchQuery) {
            params.append('address', searchQuery);
        }
        const profileId = localStorage.getItem('profileId');
        if (profileId) {
            params.append('profileId', profileId);
        }
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
        return this.fetchWithAuthorization(url)
            .then(response => response.json())
            .then(data => {
            if (!Array.isArray(data)) {
                throw new Error(`Unexpected buildings response: ${JSON.stringify(data)}`);
            }
            this.buildings(data);
        })
            .catch((error) => {
            console.error('Error fetching buildings:', error);
        })
            .finally(() => this.isLoadingBuildings(false)); // Reset loading state
    }
    // Function to fetch all external properties at login time
    fetchAllExternalProperties() {
        const profileId = localStorage.getItem('profileId');
        const externalSystem = localStorage.getItem('externalSystem') || '';
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        if (!profileId || !externalSystem) {
            console.warn('fetchAllExternalProperties: Missing profileId or externalSystem.');
            return Promise.resolve();
        }
        this.isLoadingAllProperties(true);
        const allPropertiesEndpoint = API_CONFIG.endpoints.allProperties.replace(':externalSystem', externalSystem);
        const url = `${API_CONFIG.baseUrl}${allPropertiesEndpoint}`;
        const params = new URLSearchParams();
        params.append('profileId', profileId);
        const fullUrl = `${url}?${params.toString()}`;
        return this.fetchWithAuthorization(fullUrl, {
            headers: {
                'X-API-Key': licenseKey
            }
        })
            .then(response => {
            if (!response.ok) {
                throw new Error(`Error fetching all external properties: ${response.status} ${response.statusText}`);
            }
            return response.json();
        })
            .then(data => {
            console.log('Fetched all external properties:', data.length, 'properties');
            this.allExternalProperties(data);
        })
            .catch(error => {
            console.error('Error fetching all external properties:', error);
        })
            .finally(() => this.isLoadingAllProperties(false));
    }
    // Function to look up the mapped property for a building and preselect it in the dropdown
    fetchExternalProperties() {
        const buildingId = this.selectedBuilding();
        // Nur die Antwort der letzten Mapping-Anfrage zählt (auch bei A→B→A, wo ein Vergleich der Gebäude-ID nicht reicht)
        const requestSeq = ++this.mappingRequestSeq;
        if (!buildingId) {
            console.log('fetchExternalProperties: No building selected or building deselected. Clearing properties and related data.');
            this.selectedExternalProperty(null);
            this.selectedExternalPropertyLabel('');
            this.selectedProperty(null);
            this.selectedPropertyLabel('');
            this.units([]);
            this.selectedUnit(null);
            this.selectedUnitLabel('');
            return;
        }
        const profileId = localStorage.getItem('profileId');
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        if (!profileId || !licenseKey) {
            console.error('ProfileId or LicenseKey is missing for fetching properties.');
            return;
        }
        // Ein Gebäudewechsel macht eine noch wartende Deep-Link-Einheitenauswahl gegenstandslos
        this.disposeDeepLinkUnitSubscription();
        const externalSystem = localStorage.getItem('externalSystem') || '';
        const propertiesEndpoint = API_CONFIG.endpoints.properties.replace(':externalSystem', externalSystem);
        const url = `${API_CONFIG.baseUrl}${propertiesEndpoint}`;
        const params = new URLSearchParams();
        params.append('profileId', profileId);
        params.append('buildingId', buildingId.toString());
        const fullUrl = `${url}?${params.toString()}`;
        // Reset external property selection before looking up the mapping
        this.selectedExternalProperty(null);
        this.selectedExternalPropertyLabel('');
        this.fetchWithAuthorization(fullUrl, {
            headers: {
                'X-API-Key': licenseKey
            }
        })
            .then(response => {
            if (response.status === 404) {
                // No mapping found — keep the dropdown unselected so user can pick manually
                console.log('fetchExternalProperties: No mapping found for building', buildingId, '— user must select manually.');
                return null;
            }
            if (!response.ok) {
                return response.text().then(text => {
                    throw new Error(text || `Error fetching external property: ${response.status} ${response.statusText}`);
                });
            }
            return response.json();
        })
            .then(data => {
            if (requestSeq !== this.mappingRequestSeq) {
                // Überholte Antwort (Gebäude inzwischen gewechselt): darf das Objekt der aktuellen Auswahl nicht setzen
                return;
            }
            if (data === null) {
                // Was a 404, keep unselected — a deep-link context may still supply the property
                this.applyDeepLinkPropertySelection(null);
                return;
            }
            const prop = data;
            // Preselect this property in the "Gebäude (externes System)" dropdown
            // The value must match one of the IDs from allExternalProperties
            console.log('fetchExternalProperties: Found mapped property', prop.id, 'for building', buildingId);
            this.selectedExternalProperty(String(prop.id));
            this.applyDeepLinkPropertySelection(String(prop.id));
        })
            .catch(error => {
            console.error('Error fetching external property mapping:', error);
            if (requestSeq !== this.mappingRequestSeq) {
                return; // Fehler einer überholten Anfrage
            }
            // On error, just leave unselected — user can still pick manually (deep-link context may still apply)
            this.applyDeepLinkPropertySelection(null);
        });
    }
    // ---------------------------------------------------------------------------
    // Deep-Link (Stufe 1): ?ctx=<token> → Backend löst Vorgang auf → Vorauswahl + Vorbelegung
    // ---------------------------------------------------------------------------
    /**
     * Löst den Deep-Link-Token über das Backend auf. Bewusst NICHT über fetchWithAuthorization: ein 401
     * bedeutet hier "Token ungültig" und darf den Nutzer nicht abmelden.
     */
    resolveDeepLinkContext() {
        const token = this.deepLinkToken;
        if (!token) {
            return Promise.resolve();
        }
        if (!this.mainViewModel.isUserAuthenticated()) {
            // Sitzung ist zwischenzeitlich abgelaufen (z. B. 401 beim Laden der Gebäude) → nach erneuter Anmeldung wieder versucht
            return Promise.resolve();
        }
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey() || localStorage.getItem('licenseKey') || '';
        if (!licenseKey) {
            this.deepLinkStatus('error');
            this.deepLinkMessage('Der Lizenzschlüssel fehlt – bitte melden Sie sich erneut an, um den Vorgang aus dem CRM zu übernehmen.');
            return Promise.resolve();
        }
        const profileId = localStorage.getItem('profileId');
        const accessToken = readAccessToken();
        const url = buildDeepLinkResolveUrl(API_CONFIG.baseUrl, API_CONFIG.endpoints.deeplinkResolve, token, profileId);
        // Erneuter Resolve (z. B. nach 401 und Neuanmeldung): was der vorige Kontext vorgewählt hat oder noch beobachtet,
        // darf weder bei einem Fehler stehen bleiben noch später mit dem alten Kontext zuschlagen
        this.disposeDeepLinkUnitSubscription();
        this.clearDeepLinkTicketSelection(this.deepLinkContext());
        this.deepLinkStatus('loading');
        this.deepLinkMessage('Vorgang wird aus dem CRM geladen …');
        return fetch(url, {
            method: 'GET',
            headers: {
                'X-API-Key': licenseKey,
                'Authorization': `Bearer ${accessToken}`,
                'x-api-provider': 'idwell'
            }
        })
            .then(response => {
            if (!response.ok) {
                return response.json()
                    .catch(() => ({}))
                    .then((body) => {
                    throw new DeepLinkResolveError(response.status, (body && (body.message || body.error)) || response.statusText || '');
                });
            }
            return response.json();
        })
            .then((ctx) => {
            this.applyDeepLinkContext(ctx);
        })
            .catch(error => {
            console.error('Error resolving deep-link context:', error);
            const status = error instanceof DeepLinkResolveError ? error.status : 0;
            // 409 (Vorgangstyp nicht freigegeben) trägt die Begründung des Backends – die soll der Nutzer sehen
            const detail = error instanceof DeepLinkResolveError ? (error.status === 409 ? error.message : '') : (error && error.message) || '';
            this.deepLinkContext(null);
            this.deepLinkStatus('error');
            this.deepLinkMessage(deepLinkErrorMessage(status, detail));
        });
    }
    /**
     * Wendet den aufgelösten Kontext an:
     *  1. Vorbelegungswerte (Schadentag, Hergang) in die URL — Schritt 3 liest sie per Suffix-Matching.
     *  2. Gebäude (INCON) über das gelernte Mapping; das löst die bestehende Kette
     *     selectedBuilding → fetchExternalProperties → selectedExternalProperty → fetchUnits aus.
     *  3. Objekt/Einheit/Vorgang folgen in applyDeepLinkPropertySelection (nach der Mapping-Antwort
     *     bzw. sofort, wenn kein Gebäude bekannt ist).
     */
    applyDeepLinkContext(ctx) {
        var _a;
        this.deepLinkContext(ctx);
        this.deepLinkApplications = 0;
        this.deepLinkUnlocked(false);
        this.deepLinkResidentApplied = false;
        this.disposeDeepLinkResidentSubscription();
        this.deepLinkStatus('loading');
        this.deepLinkMessage('Vorgang wird übernommen …');
        const prefill = sanitizePrefill(ctx.prefill);
        Object.keys(prefill).forEach(key => this.updateUrlParam(key, prefill[key]));
        const buildings = this.buildings();
        const building = ctx.buildingId && Array.isArray(buildings)
            ? buildings.find(b => String(b.id) === String(ctx.buildingId))
            : undefined;
        this.deepLinkBuildingPreselected = !!building;
        if (building) {
            const alreadySelected = String((_a = this.selectedBuilding()) !== null && _a !== void 0 ? _a : '') === String(building.id);
            this.selectBuilding(building); // → fetchExternalProperties → applyDeepLinkPropertySelection nach der Mapping-Antwort
            if (alreadySelected) {
                // Gleiches Gebäude (z. B. erneute Anmeldung nach 401): selectedBuilding ändert sich nicht, die Kette liefe
                // nicht an und der Hinweis bliebe bei „Vorgang wird übernommen …" stehen → Mapping selbst neu abfragen
                this.fetchExternalProperties();
            }
        }
        else {
            this.applyDeepLinkPropertySelection(null);
        }
    }
    /** Abschluss der Übernahme: Status + Hinweistext erst, wenn Einheit/Vorgang tatsächlich gesetzt sind. */
    finishDeepLinkApplication(ctx, unitPreselected, ticketPreselected, unitProblem) {
        this.deepLinkStatus(ticketPreselected ? 'applied' : 'warning');
        this.deepLinkMessage(deepLinkAppliedMessage(ctx, { buildingPreselected: this.deepLinkBuildingPreselected, unitPreselected, ticketPreselected, unitProblem }));
    }
    /**
     * Die Übernahme endet vorzeitig (Nutzer hat ein anderes Gebäude gewählt, gelerntes Mapping widerspricht dem
     * Kontext). Läuft sie noch, darf der Hinweis nicht bei „Vorgang wird übernommen …" stehen bleiben.
     */
    abortDeepLinkApplication(message) {
        this.disposeDeepLinkUnitSubscription();
        if (this.deepLinkStatus() !== 'loading') {
            return;
        }
        this.deepLinkStatus('warning');
        this.deepLinkMessage(`${message} Wenn Sie das Formular absenden, legt INCONconnect einen neuen Vorgang im CRM an.`);
    }
    /**
     * Objekt (externes System) aus dem Kontext setzen, sobald das Mapping geantwortet hat.
     * Greift nicht ein, wenn der Nutzer ein anderes Gebäude gewählt hat oder das gelernte Mapping
     * dem Kontext widerspricht (das Mapping gewinnt).
     */
    applyDeepLinkPropertySelection(mappedPropertyId) {
        var _a;
        const ctx = this.deepLinkContext();
        if (!ctx) {
            return;
        }
        const buildingId = this.selectedBuilding();
        if (ctx.buildingId && buildingId && String(buildingId) !== String(ctx.buildingId)) {
            this.abortDeepLinkApplication('Sie haben ein anderes Gebäude gewählt – Objekt, Einheit und Vorgang aus dem CRM wurden deshalb nicht übernommen.');
            return;
        }
        if (ctx.propertyId && mappedPropertyId && String(mappedPropertyId) !== String(ctx.propertyId)) {
            // Das gelernte Mapping gewinnt; der Vorgang (Einheit im anderen Objekt) lässt sich so nicht verknüpfen
            this.abortDeepLinkApplication('Das Objekt des CRM-Vorgangs passt nicht zum Objekt, das für dieses Gebäude hinterlegt ist – Objekt, Einheit und Vorgang wurden deshalb nicht übernommen. Bitte prüfen Sie Gebäude und Objekt.');
            return;
        }
        if (this.deepLinkApplications >= FormViewModel.DEEP_LINK_MAX_APPLICATIONS) {
            this.abortDeepLinkApplication('Objekt, Einheit und Vorgang aus dem CRM wurden nicht erneut übernommen.');
            return; // spätere Gebäudewechsel gehören dem Nutzer
        }
        this.deepLinkApplications++;
        if (ctx.propertyId && String((_a = this.selectedExternalProperty()) !== null && _a !== void 0 ? _a : '') !== String(ctx.propertyId)) {
            this.selectedExternalProperty(String(ctx.propertyId));
        }
        this.selectDeepLinkUnitWhenLoaded(ctx);
    }
    /**
     * Einheit wählen, sobald die Einheitenliste des Objekts geladen ist; danach den Vorgang setzen. Entschieden wird erst,
     * wenn die Liste des gewählten Objekts fertig geladen ist (`unitsLoadedFor`) — nicht nach einer festen Zeit (große
     * casavi-Objekte laden seitenweise länger als 15 s) und nicht bei der Antwort für ein anderes Objekt. Ohne gewähltes
     * Objekt und nach einem Ladefehler gibt es sofort einen Hinweis, beobachtet wird aber weiter (Objektwahl bzw. erneute
     * Objektwahl lädt neu); endgültig aufgegeben wird nur, wenn die geladene Liste die Einheit nicht enthält. Ein
     * Gebäudewechsel beendet die Beobachtung (fetchExternalProperties).
     */
    selectDeepLinkUnitWhenLoaded(ctx) {
        this.disposeDeepLinkUnitSubscription();
        if (!ctx.unitId) {
            // Ohne Einheit kann das Backend den Vorgang nicht verknüpfen (validateTicket vergleicht die Einheit)
            this.finishDeepLinkApplication(ctx, false, this.applyDeepLinkTicket(ctx, false));
            return;
        }
        const tryApply = () => {
            var _a, _b;
            // Nur die fertig geladene Liste des gewählten Objekts zählt, nie ein Rest des vorherigen Objekts
            const loaded = this.unitsLoadedFor();
            if (this.isLoadingUnits() || !loaded || loaded.propertyId !== String((_a = this.selectedProperty()) !== null && _a !== void 0 ? _a : '')) {
                return false;
            }
            const unit = this.units().find((u) => String(u.id) === String(ctx.unitId));
            if (!unit) {
                return false;
            }
            if (String((_b = this.selectedUnit()) !== null && _b !== void 0 ? _b : '') !== String(unit.id)) {
                this.selectedUnit(unit.id);
            }
            this.disposeDeepLinkUnitSubscription();
            this.finishDeepLinkApplication(ctx, true, this.applyDeepLinkTicket(ctx, true));
            return true;
        };
        // Einheit nicht wählbar → Nutzer wählt selbst. Der Vorgang wird NICHT vorgewählt: jede andere Einheit ließe
        // validateTicket scheitern, und die Einheitenwahl leert die Vorgangsauswahl ohnehin.
        const giveUp = (problem, keepWatching) => {
            if (!keepWatching) {
                this.disposeDeepLinkUnitSubscription();
            }
            this.finishDeepLinkApplication(ctx, false, this.applyDeepLinkTicket(ctx, false), problem);
        };
        /** true = entschieden (Einheit gesetzt oder endgültig aufgegeben) */
        const decide = () => {
            if (this.deepLinkContext() !== ctx) {
                this.disposeDeepLinkUnitSubscription(); // Kontext ersetzt oder verworfen
                return true;
            }
            if (tryApply()) {
                return true;
            }
            const propertyId = this.selectedProperty();
            if (propertyId === null || propertyId === undefined || propertyId === '') {
                // Nichts abzuwarten, aber weiter beobachten: wählt der Nutzer ein Objekt mit der Einheit, greift der Vorgang noch
                giveUp('no-property', true);
                return false;
            }
            const loaded = this.unitsLoadedFor();
            if (this.isLoadingUnits() || !loaded || loaded.propertyId !== String(propertyId)) {
                return false; // Liste des gewählten Objekts kommt noch
            }
            if (loaded.ok) {
                // Endgültig nur beim Objekt des Vorgangs; war es ein anderes (probeweise gewählt), weiter beobachten
                const isContextProperty = !!ctx.propertyId && loaded.propertyId === String(ctx.propertyId);
                giveUp('not-found', !isContextProperty);
                return isContextProperty;
            }
            giveUp('failed', true); // weiter beobachten: wählt der Nutzer das Objekt erneut, wird neu geladen
            return false;
        };
        if (decide()) {
            return;
        }
        this.deepLinkUnitSubscriptions = [
            this.unitsLoadedFor.subscribe(() => { decide(); }),
            this.selectedProperty.subscribe(() => { decide(); }),
        ];
        const timer = setTimeout(() => {
            if (this.deepLinkStatus() === 'loading') {
                this.deepLinkMessage('Die Einheiten des Objekts werden noch aus dem CRM geladen …');
            }
        }, FormViewModel.DEEP_LINK_UNITS_SLOW_MS);
        if (timer && typeof timer.unref === 'function') {
            timer.unref();
        }
        this.deepLinkUnitTimer = timer;
    }
    /**
     * Vorgang als einzigen Eintrag der Vorgangsauswahl vorbelegen (wie nach "bestehenden Vorgang wählen"). Nur, wenn das
     * Backend ihn beim Absenden auch verknüpfen kann und seine Einheit gewählt ist — sonst legte das Backend beim
     * Absenden stillschweigend einen zweiten CRM-Vorgang an. Liefert, ob vorgewählt wurde.
     */
    applyDeepLinkTicket(ctx, unitPreselected) {
        if (!unitPreselected || !isDeepLinkTicketLinkable(ctx)) {
            // Eine frühere Anwendung (z. B. vor einer erneuten Anmeldung) kann ihn vorgewählt haben: nicht stehen lassen,
            // sonst ginge er mit dem Absenden raus, obwohl das Banner „nicht vorausgewählt" sagt
            this.clearDeepLinkTicketSelection(ctx);
            return false;
        }
        this.availableTickets([{ id: ctx.externalTicketId, name: ctx.ticketLabel }]);
        this.externalId(ctx.externalTicketId);
        this.selectedExternalTicketDescription(ctx.ticketLabel);
        this.showExternalIdSelect(true);
        return true;
    }
    /** Entfernt eine Vorgangs-Vorauswahl, die aus diesem Kontext stammt (eine eigene Wahl des Nutzers bleibt). */
    clearDeepLinkTicketSelection(ctx) {
        var _a;
        if (ctx && ctx.externalTicketId && String((_a = this.externalId()) !== null && _a !== void 0 ? _a : '') === String(ctx.externalTicketId)) {
            this.availableTickets([]);
            this.externalId('');
            this.showExternalIdSelect(false);
        }
    }
    disposeDeepLinkUnitSubscription() {
        this.deepLinkUnitSubscriptions.forEach(subscription => subscription.dispose());
        this.deepLinkUnitSubscriptions = [];
        if (this.deepLinkUnitTimer) {
            clearTimeout(this.deepLinkUnitTimer);
            this.deepLinkUnitTimer = null;
        }
    }
    // Function to fetch units for a given property
    fetchUnits(propertyId) {
        const profileId = localStorage.getItem('profileId');
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        // Nur die Antwort der letzten Anfrage zählt: eine späte Antwort für ein inzwischen abgewähltes Objekt würde
        // sonst die Einheitenliste des aktuellen Objekts überschreiben
        const requestSeq = ++this.unitsRequestSeq;
        const finish = (units, ok) => {
            if (requestSeq !== this.unitsRequestSeq) {
                return;
            }
            this.isLoadingUnits(false);
            this.units(units);
            this.unitsLoadedFor({ propertyId: String(propertyId), ok });
        };
        if (!profileId || !licenseKey) {
            console.error('ProfileId or LicenseKey is missing for fetching units.');
            finish([], false);
            return;
        }
        this.isLoadingUnits(true);
        // Create the URL for the units endpoint from config
        const externalSystem = localStorage.getItem('externalSystem') || '';
        const unitsEndpoint = API_CONFIG.endpoints.units
            .replace(':externalSystem', externalSystem)
            .replace(':propertyId', propertyId.toString());
        const url = unitsEndpoint;
        const params = new URLSearchParams();
        params.append('profileId', profileId);
        // licenseKey will be sent in headers
        const fullUrl = `${API_CONFIG.baseUrl}${url}?${params.toString()}`;
        this.fetchWithAuthorization(fullUrl, {
            headers: {
                'X-API-Key': licenseKey // Sending licenseKey as a header
            }
        })
            .then(response => response.json())
            .then(data => {
            // Assuming data is an array of units with at least 'id' and 'name'
            // If the structure is different, this mapping might need adjustment
            finish(data.map((unit) => ({
                id: unit.id,
                name: unit.name || `Unit ${unit.id}` // Fallback if name is not present
            })), true);
        })
            .catch(error => {
            console.error('Error fetching units:', error);
            if (requestSeq !== this.unitsRequestSeq) {
                return; // Fehler einer überholten Anfrage betrifft die aktuelle Auswahl nicht
            }
            this.mainViewModel.notification(`Fehler beim Laden der Einheiten: ${error.message || 'Unbekannter Fehler'}`);
            finish([], false);
        });
    }
    // New function to fetch residents with license key in the header
    fetchResidents() {
        const propertyId = this.selectedProperty();
        const profileId = localStorage.getItem('profileId');
        if (!profileId || !propertyId) {
            console.error('ProfileId or PropertyId is missing for fetching residents.');
            this.residents([]);
            return;
        }
        this.isLoadingResidents(true); // Set loading flag to true
        // Get the license key from loginFormViewModel
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        // Create the URL for the residents endpoint from config
        const externalSystem = localStorage.getItem('externalSystem') || '';
        const residentsEndpoint = API_CONFIG.endpoints.residents
            .replace(':externalSystem', externalSystem)
            .replace(':propertyId', propertyId.toString());
        const url = residentsEndpoint;
        const params = new URLSearchParams();
        params.append('profileId', profileId);
        const fullUrl = `${API_CONFIG.baseUrl}${url}?${params.toString()}`;
        // Create headers with the license key as x-api-key
        const headers = {
            'Authorization': `Bearer ${readAccessToken() || ''}`,
            'x-api-provider': 'idwell',
            'x-api-key': licenseKey
        };
        // Make the request with the custom headers
        fetch(fullUrl, {
            method: 'GET',
            headers: headers
        })
            .then(response => {
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            return response.json();
        })
            .then(data => {
            console.log("Received residents data:", data);
            const flattenedResidents = [];
            // Helper to process resident objects
            const processResident = (resident, type, floor) => {
                return Object.assign(Object.assign({}, resident), { type: type, unitFloor: floor, isOwner: type === 'owner', 
                    // If displayName is missing, create it
                    displayName: resident.displayName || `${resident.firstName} ${resident.lastName}${resident.unitName ? ` - ${resident.unitName}` : ''}`, name: `${resident.firstName} ${resident.lastName}` // Add name for compatibility
                 });
            };
            // Handle the nested structure: { "renter": { "Floor 1": [ ... ] }, "owner": { ... } }
            // We verify if data is an object and not an array
            if (typeof data === 'object' && !Array.isArray(data)) {
                Object.keys(data).forEach(typeKey => {
                    const floors = data[typeKey];
                    if (typeof floors === 'object' && floors !== null) {
                        Object.keys(floors).forEach(floorKey => {
                            const residentsInFloor = floors[floorKey];
                            if (Array.isArray(residentsInFloor)) {
                                residentsInFloor.forEach(resident => {
                                    flattenedResidents.push(processResident(resident, typeKey, floorKey));
                                });
                            }
                        });
                    }
                });
            }
            else if (Array.isArray(data)) {
                // Fallback for old array format if necessary
                data.forEach((resident) => {
                    flattenedResidents.push(Object.assign(Object.assign({}, resident), { isOwner: resident.type === 'owner', displayName: resident.displayName || `${resident.firstName} ${resident.lastName}${resident.unitName ? ` - ${resident.unitName}` : ''}`, name: `${resident.firstName} ${resident.lastName}` }));
                });
            }
            console.log("Processed residents data:", flattenedResidents);
            this.residents(flattenedResidents);
        })
            .catch(error => {
            console.error('Error fetching residents:', error);
            this.mainViewModel.notification(`Fehler beim Laden der Bewohner: ${error.message || 'Unbekannter Fehler'}`);
        })
            .finally(() => this.isLoadingResidents(false)); // Reset loading state
    }
    // Check if a field should use the multi-select component
    isResidentField(fieldName) {
        const suffixes = ['_BetroffenerMieter', '_BetroffenerEigentümer', '_VerursachenderEigentümer', '_VerursachenderMieter'];
        return suffixes.some(suffix => fieldName.endsWith(suffix));
    }
    // Check if a field is for owners
    isOwnerField(fieldName) {
        return fieldName.includes('Eigentümer');
    }
    // Get filtered residents based on field type
    getFilteredResidentsForField(fieldName) {
        if (this.isOwnerField(fieldName)) {
            // Filter to only show owners - make sure we're comparing strings or booleans
            return this.residents().filter(resident => resident.type === 'owner' || resident.isOwner === true);
        }
        else {
            // Filter to only show tenants (renters)
            return this.residents().filter(resident => resident.type === 'renter' || (resident.isOwner === false && resident.type !== 'owner'));
        }
    }
    // Add a custom resident
    addCustomResident(fieldName, customValue) {
        console.log(`[RMS-DEBUG][${fieldName}] addCustomResident: Called with value="${customValue}"`);
        // Create a temporary resident entry for the custom value
        const customResident = {
            id: `custom-${Date.now()}`, // Use string ID
            type: this.isOwnerField(fieldName) ? 'owner' : 'renter',
            firstName: customValue,
            lastName: '',
            email: null,
            telephone: '',
            unitName: '',
            unitFloor: '',
            unitId: '',
            displayName: customValue,
            name: customValue, // Set name property
            isOwner: this.isOwnerField(fieldName),
            isCustom: true
        };
        console.log(`[RMS-DEBUG][${fieldName}] addCustomResident: Created custom resident id=${customResident.id}`);
        // Add to the custom residents array
        const customResidents = Object.assign({}, this.customResidents());
        if (!customResidents[fieldName]) {
            customResidents[fieldName] = [];
        }
        customResidents[fieldName].push(customResident);
        this.customResidents(customResidents);
        console.log(`[RMS-DEBUG][${fieldName}] addCustomResident: customResidents count now=${customResidents[fieldName].length}`);
        // Update the field value
        this.updateResidentFieldValue(fieldName);
    }
    // Toggle a resident selection
    toggleResidentSelection(fieldName, resident) {
        console.log(`[RMS-DEBUG][${fieldName}] toggleResidentSelection: Called for resident id=${resident.id}, name="${resident.name}"`);
        const currentSelections = this.selectedResidents();
        const fieldSelections = currentSelections[fieldName] || [];
        // Check if resident is already selected
        const isAlreadySelected = fieldSelections.some(r => r.id === resident.id);
        console.log(`[RMS-DEBUG][${fieldName}] toggleResidentSelection: isAlreadySelected=${isAlreadySelected}, current count=${fieldSelections.length}`);
        // Create a completely new array (immutable) — never mutate the existing one.
        // This guarantees Knockout detects the change in all browsers.
        const newFieldSelections = isAlreadySelected
            ? fieldSelections.filter(r => r.id !== resident.id) // Remove: creates new array
            : [...fieldSelections, resident]; // Add: creates new array
        // Create a new outer object with the new array
        const newSelections = Object.assign(Object.assign({}, currentSelections), { [fieldName]: newFieldSelections });
        console.log(`[RMS-DEBUG][${fieldName}] toggleResidentSelection: New count=${newFieldSelections.length}, new IDs=`, JSON.stringify(newFieldSelections.map(r => r.id)));
        console.log(`[RMS-DEBUG][${fieldName}] toggleResidentSelection: About to call selectedResidents() with new object. Object keys=`, Object.keys(newSelections));
        this.selectedResidents(newSelections);
        console.log(`[RMS-DEBUG][${fieldName}] toggleResidentSelection: selectedResidents() updated. Verification: count=${(this.selectedResidents()[fieldName] || []).length}`);
        this.updateResidentFieldValue(fieldName);
    }
    // Update the input field value with selected residents
    updateResidentFieldValue(fieldName) {
        console.log(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: ===== Called =====`);
        // Get selected residents for this field
        const selections = this.selectedResidents()[fieldName] || [];
        // Get custom residents for this field
        const customEntries = this.customResidents()[fieldName] || [];
        // Combine all selected residents
        const allSelections = [...selections, ...customEntries];
        console.log(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: selections=${selections.length}, customEntries=${customEntries.length}, total=${allSelections.length}`);
        // Format with more details and comma separation
        const formattedValue = allSelections
            .map(resident => {
            // For custom entries
            if (resident.isCustom) {
                return '- ' + (resident.displayName || resident.name || '');
            }
            // For regular entries, include name, email, and phone
            let details = [];
            // Add full name
            const fullName = `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
            if (fullName)
                details.push(fullName);
            // Add email if available
            if (resident.email)
                details.push(resident.email);
            // Add phone if available
            if (resident.telephone && resident.telephone.trim())
                details.push(resident.telephone);
            // Add address if available
            if (resident.address) {
                const addrParts = [];
                if (resident.address.street)
                    addrParts.push(resident.address.street);
                if (resident.address.zip || resident.address.city) {
                    addrParts.push(`${resident.address.zip || ''} ${resident.address.city || ''}`.trim());
                }
                if (addrParts.length > 0) {
                    details.push(addrParts.join(', '));
                }
            }
            // Join with commas
            return '- ' + details.join(', ');
        })
            .join('<br />'); // use newline between different entries
        console.log(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: formattedValue="${formattedValue.substring(0, 200)}${formattedValue.length > 200 ? '...' : ''}"`);
        // Find the field and update its value
        const field = this.incidentFields().find(f => f.name === fieldName);
        if (field) {
            const oldValue = field.value();
            field.value(formattedValue);
            console.log(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: Field FOUND. Old value length=${(oldValue || '').length}, New value length=${formattedValue.length}`);
            console.log(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: Field value after set="${field.value().substring(0, 200)}${field.value().length > 200 ? '...' : ''}"`);
        }
        else {
            console.error(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: *** FIELD NOT FOUND *** This is likely the root cause!`);
            console.error(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: Available field names:`, this.incidentFields().map(f => f.name));
            console.error(`[RMS-DEBUG][${fieldName}] updateResidentFieldValue: Exact fieldName being searched="${fieldName}", length=${fieldName.length}, charCodes=`, Array.from(fieldName).map(c => c.charCodeAt(0)));
        }
    }
    fetchContactDetails(residentIds) {
        if (!residentIds || residentIds.length === 0) {
            return Promise.resolve([]);
        }
        const profileId = localStorage.getItem('profileId');
        const externalSystem = localStorage.getItem('externalSystem') || 'casavi'; // Default to casavi if not found, but should exist
        // Replace placeholder in endpoint
        let url = API_CONFIG.endpoints.contactDetails.replace(':externalSystem', externalSystem);
        url = `${API_CONFIG.baseUrl}${url}`;
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        this.isFetchingDetails(true); // Set fetching flag
        return this.fetchWithAuthorization(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': licenseKey
            },
            body: JSON.stringify({
                residentIds: residentIds,
                profileId: profileId
            })
        })
            .then(response => {
            if (!response.ok) {
                // Log error but allow flow to continue (maybe returning empty list)
                console.error("Failed to fetch contact details", response.statusText);
                return [];
            }
            return response.json();
        })
            .then(data => {
            // Merge data back into residents observable
            // This ensures that our local state has the latest info
            const allResidents = this.residents();
            let updatedAny = false;
            data.forEach((contact) => {
                const match = allResidents.find(r => r.id === contact.id);
                if (match) {
                    // Update fields
                    match.email = contact.email || match.email;
                    match.telephone = contact.telephone || match.telephone;
                    match.address = contact.address || match.address;
                    updatedAny = true;
                }
            });
            if (updatedAny) {
                this.residents.valueHasMutated(); // Notify subscribers
            }
            return data;
        })
            .catch(err => {
            console.error("Error fetching contact details:", err);
            return [];
        })
            .finally(() => {
            this.isFetchingDetails(false); // Reset fetching flag
        });
    }
    // ===== Service providers (Dienstleister) =====
    // Fetch the mandant-wide list of service providers (one call, full contact data)
    fetchServiceProviders() {
        const externalSystem = localStorage.getItem('externalSystem') || '';
        const licenseKey = this.mainViewModel.loginFormViewModel.licenseKey();
        if (!externalSystem || !licenseKey) {
            console.warn('fetchServiceProviders: Missing externalSystem or licenseKey.');
            this.serviceProviders([]);
            return Promise.resolve();
        }
        this.isLoadingServiceProviders(true);
        const providersEndpoint = API_CONFIG.endpoints.providers.replace(':externalSystem', externalSystem);
        const url = `${API_CONFIG.baseUrl}${providersEndpoint}`;
        const headers = {
            'Authorization': `Bearer ${readAccessToken() || ''}`,
            'x-api-provider': 'idwell',
            'X-API-Key': licenseKey
        };
        return fetch(url, { method: 'GET', headers: headers })
            .then(response => {
            if (response.status === 401) {
                this.handleUnauthorized();
                return Promise.reject('Unauthorized');
            }
            if (!response.ok) {
                // Try to surface the { error: "..." } body for 500s
                return response.json()
                    .catch(() => null)
                    .then(body => {
                    const message = body && body.error ? body.error : `${response.status} ${response.statusText}`;
                    throw new Error(message);
                });
            }
            return response.json();
        })
            .then((data) => {
            const providers = (Array.isArray(data) ? data : []).map(provider => (Object.assign(Object.assign({}, provider), { phones: Array.isArray(provider.phones) ? provider.phones : [], displayName: provider.name })));
            this.serviceProviders(providers);
        })
            .catch(error => {
            if (error === 'Unauthorized')
                return;
            console.error('Error fetching service providers:', error);
            this.mainViewModel.notification(`Fehler beim Laden der Dienstleister: ${error.message || 'Unbekannter Fehler'}`);
            this.serviceProviders([]);
        })
            .finally(() => this.isLoadingServiceProviders(false));
    }
    // Check if a field should use the service provider multi-select component
    isServiceProviderField(fieldName) {
        return fieldName.endsWith('Installateurname') || fieldName.endsWith('Sanierername');
    }
    // All service providers are eligible for every provider field (no owner/tenant split)
    getFilteredServiceProvidersForField(_fieldName) {
        return this.serviceProviders();
    }
    // Add a custom service provider entry
    addCustomServiceProvider(fieldName, customValue) {
        const customProvider = {
            id: `custom-${Date.now()}`,
            sourceSystem: '',
            type: null,
            name: customValue,
            email: null,
            phones: [],
            website: null,
            address: null,
            displayName: customValue,
            isCustom: true
        };
        const customProviders = Object.assign({}, this.customServiceProviders());
        if (!customProviders[fieldName]) {
            customProviders[fieldName] = [];
        }
        customProviders[fieldName] = [...customProviders[fieldName], customProvider];
        this.customServiceProviders(customProviders);
        this.updateServiceProviderFieldValue(fieldName);
    }
    // Toggle a service provider selection (immutable update so Knockout detects the change)
    toggleServiceProviderSelection(fieldName, provider) {
        const currentSelections = this.selectedServiceProviders();
        const fieldSelections = currentSelections[fieldName] || [];
        const isAlreadySelected = fieldSelections.some(p => p.id === provider.id);
        const newFieldSelections = isAlreadySelected
            ? fieldSelections.filter(p => p.id !== provider.id)
            : [...fieldSelections, provider];
        this.selectedServiceProviders(Object.assign(Object.assign({}, currentSelections), { [fieldName]: newFieldSelections }));
        this.updateServiceProviderFieldValue(fieldName);
    }
    // Update the input field value with the selected service providers
    updateServiceProviderFieldValue(fieldName) {
        const selections = this.selectedServiceProviders()[fieldName] || [];
        const customEntries = this.customServiceProviders()[fieldName] || [];
        const allSelections = [...selections, ...customEntries];
        const formattedValue = allSelections
            .map(provider => {
            if (provider.isCustom) {
                return '- ' + (provider.displayName || provider.name || '');
            }
            const details = [];
            if (provider.name)
                details.push(provider.name);
            if (provider.email)
                details.push(provider.email);
            // Add ALL phone numbers, each labelled by its type
            if (Array.isArray(provider.phones)) {
                provider.phones.forEach(phone => {
                    if (phone && phone.value && phone.value.trim()) {
                        details.push(phone.type ? `${phone.type}: ${phone.value}` : phone.value);
                    }
                });
            }
            // Add address if available
            if (provider.address) {
                const addrParts = [];
                if (provider.address.street)
                    addrParts.push(provider.address.street);
                if (provider.address.zip || provider.address.city) {
                    addrParts.push(`${provider.address.zip || ''} ${provider.address.city || ''}`.trim());
                }
                if (addrParts.length > 0) {
                    details.push(addrParts.join(', '));
                }
            }
            return '- ' + details.join(', ');
        })
            .join('<br />');
        const field = this.incidentFields().find(f => f.name === fieldName);
        if (field) {
            field.value(formattedValue);
        }
        else {
            console.error(`updateServiceProviderFieldValue: Field not found for "${fieldName}". Available:`, this.incidentFields().map(f => f.name));
        }
    }
    fetchContracts() {
        const buildingId = this.selectedBuilding();
        if (!buildingId) {
            this.contracts([]);
            this.selectedContract(null);
            this.selectedContractLabel('');
            return;
        }
        const baseUrl = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.contracts}`;
        const params = new URLSearchParams();
        params.append('building', buildingId.toString());
        const profileId = localStorage.getItem('profileId');
        if (profileId) {
            params.append('profileId', profileId);
        }
        this.fetchWithAuthorization(`${baseUrl}?${params.toString()}`)
            .then(response => response.json())
            .then(data => this.contracts(data))
            .catch(error => console.error('Error fetching contracts:', error));
    }
    fetchIncidentCategories() {
        this.fetchWithAuthorization(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.incidentCategories}`)
            .then(response => response.json())
            .then(data => this.incidentCategories(data))
            .catch(error => console.error('Error fetching incident categories:', error));
    }
    fetchIncidentFields() {
        const categoryPath = this.constructCategoryPath();
        // Fetch incident fields based on the selected category path
        this.fetchAsApiUser(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.incidentFields}?category=${categoryPath}`)
            .then(response => response.json())
            .then(data => {
            const fields = data.questions.map(createIncidentField); // Convert each field in the response to an IncidentField object
            // Zurück auf Schritt 2 und wieder vor lädt den Katalog neu: bereits Eingegebenes je Feldname übernehmen
            const previous = new Map(this.incidentFields().map(field => [field.name, field.value()]));
            this.incidentFields(fields);
            this.requestMaker(data.requestMaker); // Store the requestMaker
            // After populating incidentFields, check for special fields
            this.incidentFields().forEach(field => {
                const fieldNameLower = field.name.toLowerCase();
                if (fieldNameLower.endsWith('_nameap')) {
                    field.value(this.getFromLocalStorage('user_name'));
                }
                else if (fieldNameLower.endsWith('_emailap')) {
                    field.value(this.getFromLocalStorage('user_email'));
                }
                else if (fieldNameLower.endsWith('_nummer') || fieldNameLower.endsWith('_nummerap')) {
                    field.value(this.getFromLocalStorage('user_phone'));
                }
                const kept = previous.get(field.name);
                if (kept !== undefined && kept !== '' && kept !== null) {
                    field.value(kept);
                }
            });
            this.applyHergangFallback();
            this.applyDeepLinkResidentPreselection();
        })
            .catch(error => console.error('Error fetching incident fields:', error));
    }
    /**
     * Deep-Link (Stufe 1): Der Melder des CRM-Vorgangs („Gemeldet von") wird als betroffene(r) Mieter bzw.
     * Eigentümer vorbelegt, sofern er in der Bewohnerliste des Objekts steht (die IDs sind casavi-Kontakt-IDs).
     * Ob jemand „verursachend" ist, kennt das CRM nicht – das bleibt Handarbeit. Greift je Kontext einmal und nur,
     * solange der Nutzer in dem Feld noch nichts gewählt hat; Kontaktdaten werden wie beim manuellen Wählen nachgeladen.
     */
    applyDeepLinkResidentPreselection() {
        const ctx = this.deepLinkContext();
        const contactId = ctx && ctx.contact ? ctx.contact.id : null;
        if (!contactId || this.deepLinkResidentApplied) {
            return;
        }
        const fields = this.incidentFields();
        if (!fields.some(field => this.isResidentField(field.name))) {
            return; // Schadenart ohne Bewohnerfelder
        }
        const residents = this.residents();
        if (residents.length === 0) {
            // Bewohnerliste lädt noch → einmal nachziehen, sobald sie da ist
            this.disposeDeepLinkResidentSubscription();
            this.deepLinkResidentSubscription = this.residents.subscribe(() => {
                this.disposeDeepLinkResidentSubscription();
                this.applyDeepLinkResidentPreselection();
            });
            return;
        }
        this.deepLinkResidentApplied = true;
        const resident = residents.find(r => String(r.id) === String(contactId));
        if (!resident) {
            return;
        }
        const suffix = resident.type === 'owner' || resident.isOwner === true ? '_BetroffenerEigentümer' : '_BetroffenerMieter';
        const field = fields.find(f => f.name.endsWith(suffix));
        if (!field) {
            return;
        }
        const chosen = this.selectedResidents()[field.name] || [];
        const custom = this.customResidents()[field.name] || [];
        if (chosen.length > 0 || custom.length > 0) {
            return; // Nutzer hat bereits gewählt
        }
        this.selectedResidents(Object.assign(Object.assign({}, this.selectedResidents()), { [field.name]: [resident] }));
        this.updateResidentFieldValue(field.name);
        this.fetchContactDetails([resident.id])
            .then(() => this.updateResidentFieldValue(field.name))
            .catch(() => undefined);
    }
    disposeDeepLinkResidentSubscription() {
        if (this.deepLinkResidentSubscription) {
            this.deepLinkResidentSubscription.dispose();
            this.deepLinkResidentSubscription = null;
        }
    }
    /**
     * Schadenarten ohne `*_Hergang`-Feld (z. B. Leitungswasser) würden den Hergang aus der URL (Deep-Link-Prefill
     * oder manueller Parameter) verlieren. Dann landet er im ersten noch leeren `*_Sonstiges`-Feld
     * („Sonstige Information"). Hat die Schadenart ein Hergang-Feld, greift wie bisher das Suffix-Matching.
     */
    applyHergangFallback() {
        const hergangParam = Array.from(new URLSearchParams(window.location.search).entries())
            .find(([key]) => key.toLowerCase() === 'hergang');
        const hergang = hergangParam ? hergangParam[1] : '';
        if (!hergang) {
            return;
        }
        const fields = this.incidentFields();
        if (fields.some(field => field.name.toLowerCase().endsWith('hergang'))) {
            return;
        }
        const target = fields.find(field => field.name.toLowerCase().endsWith('sonstiges') && !field.value());
        if (target) {
            target.value(hergang);
        }
    }
    constructCategoryPath() {
        // Construct the path based on selected categories
        let path = '';
        if (this.selectedLevel1Category()) {
            path += this.selectedLevel1Category();
            if (this.selectedLevel2Category()) {
                path += `.${this.selectedLevel2Category()}`;
                if (this.selectedLevel3Category()) {
                    path += `.${this.selectedLevel3Category()}`;
                }
            }
        }
        return path;
    }
    processRequestMakerData(requestMakerData) {
        if (!requestMakerData) {
            return {}; // Handle case where requestMakerData is null or undefined
        }
        let title = requestMakerData.title;
        let text = requestMakerData.text;
        // Replace special parameters
        title = title.replace("{{building_address}}", this.selectedBuildingLabel());
        text = text.replace("{{building_address}}", this.selectedBuildingLabel());
        title = title.replace("{{flat_number}}", this.selectedContractLabel());
        text = text.replace("{{flat_number}}", this.selectedContractLabel());
        // Add resident info if available
        if (this.selectedResidentLabel()) {
            title = title.replace("{{resident_name}}", this.selectedResidentLabel());
            text = text.replace("{{resident_name}}", this.selectedResidentLabel());
        }
        // Replace other placeholders with field values
        this.incidentFields().forEach(field => {
            const fieldValue = field.value();
            if (field.name.endsWith('Vorgangsnummer')) {
                // Only replace if we have a selected external ticket and leave placeholder otherwise
                // Placeholder is replaced by backend afterwards
                if (this.selectedExternalTicketDescription()) {
                    title = title.replace(`{{${field.name}}}`, this.selectedExternalTicketDescription());
                    text = text.replace(`{{${field.name}}}`, this.selectedExternalTicketDescription());
                }
            }
            else {
                title = title.replace(`{{${field.name}}}`, fieldValue);
                text = text.replace(`{{${field.name}}}`, fieldValue);
            }
        });
        return {
            title: title,
            text: text,
            status: requestMakerData.status,
            category: requestMakerData.category
        };
    }
    fetchAsApiUser(url) {
        // Retrieve the accessToken from local storage (may already be gone after a 401 → never throw here)
        const accessToken = readAccessToken();
        if (!accessToken) {
            return Promise.reject('Kein Access Token vorhanden. Anmeldung erforderlich');
        }
        // Make the request with the access token
        return fetch(`${url}`, {
            method: 'GET',
            headers: {
                'x-api-provider': 'idwell',
                'Authorization': `Bearer ${accessToken}`
            }
        });
    }
    // Centralized function to handle fetch calls and 401 responses
    fetchWithAuthorization(url, options = {}) {
        // A delayed call (e.g. the rate-limited building search) may run after the token was removed on a 401:
        // a missing token must lead to handleUnauthorized(), not to a JSON.parse exception inside a subscription.
        const accessToken = readAccessToken();
        if (!accessToken) {
            this.handleUnauthorized();
            return Promise.reject('Kein Access Token vorhanden.');
        }
        // Add Authorization header
        options.headers = Object.assign(Object.assign({}, options.headers), { 'Authorization': `Bearer ${accessToken}`, 'x-api-provider': 'idwell' });
        return fetch(url, options).then(response => {
            if (response.status === 401) {
                this.handleUnauthorized();
                return Promise.reject('Unauthorized');
            }
            return response;
        }).catch(error => {
            console.error('Error:', error);
            throw error; // Re-throw the error after logging it
        });
    }
    handleUnauthorized() {
        // Clear user authentication and token
        localStorage.removeItem('accessToken');
        this.mainViewModel.isUserAuthenticated(false);
        // Show notification and redirect to login
        this.mainViewModel.notification("Sitzung veraltet. Anmeldung erforderlich.");
        this.mainViewModel.goToStep1(); // Redirect to login page
    }
    updateUrlParam(key, value) {
        // Update the URL with the new parameter value
        const url = new URL(window.location.href);
        if (value) {
            // The .set() method handles encoding automatically.
            url.searchParams.set(key, value);
        }
        else {
            // Remove the param if the value is empty or null
            url.searchParams.delete(key);
        }
        // Update the URL without reloading the page
        window.history.replaceState({}, '', url);
    }
    // Helper function to save data to localStorage
    saveToLocalStorage(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        }
        catch (e) {
            console.error("Error saving to localStorage", e);
        }
    }
    // Helper function to get data from localStorage
    getFromLocalStorage(key) {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        }
        catch (e) {
            console.error("Error reading from localStorage", e);
            return null;
        }
    }
}
/** Nach dieser Zeit ohne Einheitenliste zeigt der Hinweis, dass noch geladen wird (aufgegeben wird nie vorher). */
FormViewModel.DEEP_LINK_UNITS_SLOW_MS = 15000;
FormViewModel.DEEP_LINK_MAX_APPLICATIONS = 2;
// Handling clicks outside the dropdown
document.addEventListener('click', function (event) {
    const inputElement = document.querySelector('#buildingSearch');
    if (inputElement) {
        const container = inputElement.closest('.relative');
        if (container && !container.contains(event.target)) {
            const viewModel = ko.dataFor(container);
            if (viewModel && viewModel.dropdownVisible) {
                viewModel.dropdownVisible(false);
            }
        }
    }
});
export default FormViewModel;
